import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { MailDeliveryStatus } from '../user/user.service';
import { StaffMessageEventsService } from './staff-message-events.service';

/** list() (the staff dashboard queue) returns CUST2STAFF, STAFF2STAFF, and STAFF2ORDER notes addressed to the
 * caller (e.g. issue #66's "Baum ohne Zahlung abgeholt" note to CONTROLLER - see
 * PickupTourItemController.notifyControllersOfUnpaidPickup()); listForOrder() can additionally return SYSTEM
 * (order-confirmation/change) and STAFF2CUST (a staff reply) since it's the full per-order history. */
export type StaffMessageType = 'CUST2STAFF' | 'STAFF2STAFF' | 'STAFF2CUST' | 'SYSTEM' | 'STAFF2ORDER' | 'STAFF2TOUR' | 'ORDER_NOTE' | 'ORDER_AUDIT';
/** Roles a crew member may address a message about a stop to. */
export type StaffTargetRole = 'PLANNER' | 'OFFICER' | 'CONTROLLER';
export type StaffMessageState ='NEW' | 'OPEN' | 'SENT' | 'READ' | 'CONFIRMED';

export interface StaffMessage {
  id: string;
  messageType: StaffMessageType;
  messageState: StaffMessageState;
  orderNr: string | null;
  subject: string | null;
  senderName: string;
  senderEmail: string | null;
  recipientName: string | null;
  dateCreated: string;
  body: string | null;
  outgoing: boolean;
  /** True once an email was actually dispatched for this message (not just requested). */
  mailSent: boolean;
  /** The pickup tour the message belongs to (tour messages, and crew messages about an item). */
  tourId: string | null;
  tourName: string | null;
  /** The pickup item (stop) the message is about, for tour messages and crew messages written at a stop. */
  pickupItemId: string | null;
  /** Role the message is addressed to instead of a person (PLANNER = a crew reply to a tour message). */
  targetRole: string | null;
  /** Id of the message this one answers, if any. */
  threadId: string | null;
  /** False when the caller only gets to read it (e.g. another crew member's answer, or their own tour message) - no reply/erledigt then. */
  canAct: boolean;
  /** Only for a tour message the caller wrote: how many people it is addressed to, and who of them has read it. */
  recipientCount: number | null;
  readBy: string[];
}

export interface StaffUserOption {
  id: string;
  name: string;
  email: string;
  mailDeliveryStatus: MailDeliveryStatus;
}

export interface StaffMessageStats {
  unreadCount: number;
  unconfirmedCount: number;
  totalCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class StaffMessageService {
  private readonly apiUrl = `${environment.apiUrl}/messages/staff`;
  private readonly events = inject(StaffMessageEventsService);

  constructor(private readonly http: HttpClient) {
  }

  /** Returns every dashboard message regardless of state - open/CONFIRMED filtering is a client-side
   * concern (the user's showConfirmedMessages preference). Erledigte messages older
   * than the server's window (30 days) only come with all = true.  */
  list(all = false): Observable<StaffMessage[]> {
    return this.http.get<StaffMessage[]>(this.apiUrl, { params: all ? { all: true } : {} });
  }

  /** Full message history for one order (issue #26) - unlike list(), not restricted to the CUST2STAFF/
   * STAFF2STAFF dashboard queue. */
  listForOrder(orderNr: string): Observable<StaffMessage[]> {
    return this.http.get<StaffMessage[]>(`${environment.apiUrl}/orders/${orderNr}/messages`);
  }


  /** Ids of unread incoming messages in the staff queue - see NewMessageAlertService. */
  unreadIds(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/unread-ids`);
  }

  /** Planning staff's messages to one tour's crew (STAFF2TOUR). */
  listForTour(tourId: string): Observable<StaffMessage[]> {
    return this.http.get<StaffMessage[]>(`${environment.apiUrl}/pickup-tours/${tourId}/messages`);
  }

  sendToTour(tourId: string, subject: string, body: string): Observable<StaffMessage> {
    return this.http.post<StaffMessage>(`${environment.apiUrl}/pickup-tours/${tourId}/messages`, { subject, body });
  }

  /** The conversation about one stop of a tour: planner -> crew, crew -> staff roles, and the answers. */
  listForItem(tourId: string, itemId: string): Observable<StaffMessage[]> {
    return this.http.get<StaffMessage[]>(`${environment.apiUrl}/pickup-tours/${tourId}/items/${itemId}/messages`);
  }

  /** Planner -> the tour's crew, about one stop. */
  sendToCrewAboutItem(tourId: string, itemId: string, subject: string, body: string): Observable<StaffMessage> {
    return this.http.post<StaffMessage>(
      `${environment.apiUrl}/pickup-tours/${tourId}/items/${itemId}/messages`, { subject, body });
  }

  /** Crew -> staff, about one stop: one message per chosen role, each handled independently. */
  sendToStaffAboutItem(tourId: string, itemId: string, roles: StaffTargetRole[], subject: string,
                       body: string): Observable<StaffMessage[]> {
    return this.http.post<StaffMessage[]>(
      `${environment.apiUrl}/pickup-tours/${tourId}/items/${itemId}/messages/to-staff`, { roles, subject, body });
  }

  /** Internal note on an order (PLANNER/OFFICER/MANAGER/ADMIN) - nothing to read or confirm. */
  addOrderNote(orderNr: string, subject: string | null, body: string): Observable<StaffMessage> {
    return this.http.post<StaffMessage>(`${environment.apiUrl}/orders/${orderNr}/messages/notes`, { subject, body });
  }

  /** A message from staff to the customer of the order, written in the context of the order; sendMail also
   * notifies them by email. */
  sendToCustomer(orderNr: string, body: string, sendMail: boolean): Observable<StaffMessage> {
    return this.http.post<StaffMessage>(`${environment.apiUrl}/orders/${orderNr}/messages/customer`, { body, sendMail });
  }

  deleteOrderNote(orderNr: string, noteId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/orders/${orderNr}/messages/notes/${noteId}`);
  }

  listRecipients(): Observable<StaffUserOption[]> {
    return this.http.get<StaffUserOption[]>(`${this.apiUrl}/recipients`);
  }

  /** Dashboard-Tile counts (issue #11). */
  getStats(): Observable<StaffMessageStats> {
    return this.http.get<StaffMessageStats>(`${this.apiUrl}/stats`);
  }

  markRead(id: string): Observable<StaffMessage> {
    return this.http.patch<StaffMessage>(`${this.apiUrl}/${id}/read`, {})
      .pipe(tap(() => this.events.notifyChanged()));
  }

  /** Context-menu "Markiere ungelesen" - only offered for messages currently READ. */
  markUnread(id: string): Observable<StaffMessage> {
    return this.http.patch<StaffMessage>(`${this.apiUrl}/${id}/unread`, {})
      .pipe(tap(() => this.events.notifyChanged()));
  }

  /** sendMail is only honored server-side for MANAGER/ADMIN callers - see StaffMessageController. */
  sendToColleague(toUserId: string, subject: string, body: string, sendMail: boolean): Observable<void> {
    return this.http.post<void>(this.apiUrl, { toUserId, subject, body, sendMail })
      .pipe(tap(() => this.events.notifyChanged()));
  }

  reply(id: string, body: string, sendMail: boolean): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/reply`, { body, sendMail })
      .pipe(tap(() => this.events.notifyChanged()));
  }

  confirm(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/confirm`, {})
      .pipe(tap(() => this.events.notifyChanged()));
  }

  unconfirm(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/unconfirm`, {})
      .pipe(tap(() => this.events.notifyChanged()));
  }
}
